import {
  PDFDocument, StandardFonts, rgb,
  PDFName, PDFDict, PDFArray, PDFStream, PDFNumber,
  pushGraphicsState, popGraphicsState, concatTransformationMatrix, drawObject,
} from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { searchCarriers } from '../../api/figApi';
import { resolveCarrierShortname, CARRIER_SHORTNAMES_BY_NAIC } from '../../utils/carrierShortnameMap';

// Reverse-map: shortname → NAIC. Built once per module load. Used when the
// policy has no NAICCode populated — resolve the carrier to a shortname, then
// find a NAIC whose mapped shortname matches. Carriers like Lloyd's have no
// US NAIC and correctly return undefined (→ blank field on the ACORD).
const NAIC_BY_SHORTNAME = Object.entries(CARRIER_SHORTNAMES_BY_NAIC)
  .reduce((acc, [naic, short]) => { acc[short] = naic; return acc; }, {});

// FIG working copy of the ACORD 140 (scripts/acord140-template-surgery.mjs):
// static "Page N of 3" footers removed from pages 2–3 and the two phantom
// Producer_CustomerIdentifier_A field entries dropped. The original blank is
// kept untouched at /acord/ACORD_140_blank.pdf.
const BLANK_PDF_URL = '/acord/ACORD_140_fig_v2.pdf';

// Producer signature: drawn in a script face and baked into the signature
// page (not a form field). OFL-licensed, bundled under public/fonts/.
const SIGNATURE_FONT_URL = '/fonts/GreatVibes-Regular.ttf';
// fontkit's subsetter drops Allura's glyphs (blank output); Great Vibes and
// Alex Brush subset cleanly. Set false if switching to Allura.
const SIGNATURE_FONT_SUBSET = true;
const SIGNATURE_TEXT = 'Marc Fragos';

/**
 * Generate a filled ACORD 140 — Property Section.
 *
 * The blank ACORD 140 is a 3-page form (page 1 = Premises A, page 2 =
 * Additional Premises B, page 3 = signature). Every FIG_Buildings row for the
 * insured renders in ONE output PDF: building 1 on page 1, building 2 on page
 * 2, and each further building on its own copy of the Additional Premises
 * page, followed by the signature page.
 *
 * Signature page: Producer signature is drawn in a script face and baked. The
 * Applicant (Named Insured) signature and date are intentionally LEFT BLANK
 * so DocuSign can route the form for live signature at signing time.
 */
export default async function generateACORD140(formData, insured, getToken, policy) {
  const res = await fetch(BLANK_PDF_URL);
  if (!res.ok) throw new Error(`Failed to load blank ACORD 140 (${res.status})`);
  const blankBytes = await res.arrayBuffer();

  // Resolve NAIC: policy.NAICCode first; else reverse-lookup via the carrier
  // shortname map. Lloyd's and other no-US-NAIC carriers fall through to ''.
  const resolvedShort = resolveCarrierShortname({
    policyCarrier: formData.carrier || '',
    policyNAIC: formData.naicCode || '',
  });
  const resolvedNAIC = formData.naicCode || NAIC_BY_SHORTNAME[resolvedShort?.shortname] || '';

  // Carrier address lookup — one call, cached by searchCarriers. Prefer exact
  // NAIC match; otherwise fall back to the top name-based hit.
  let carrierMatch = null;
  if (getToken) {
    try {
      const token = await getToken();
      const results = await searchCarriers(token, formData.carrier || '');
      if (resolvedNAIC) {
        carrierMatch = results.find(c => String(c.NAICCode) === String(resolvedNAIC)) || null;
      }
      if (!carrierMatch) carrierMatch = results[0] || null;
    } catch {}
  }

  // Form 1 carries building 1 (page 1) and building 2 (page 2). Each further
  // building is filled into the Additional Premises slot (loc B) of a fresh
  // copy of the form and only that page is taken. All pages are merged into a
  // single PDF: p1(bldg 1), p2(bldg 2), p2'(bldg 3), ..., signature.
  const allLocs = applyPolicyDrivenRows(formData.locs || [], policy);
  const firstBytes = await fillOneForm(blankBytes, formData, insured, allLocs[0], allLocs[1], carrierMatch, resolvedNAIC);
  const extraBytes = [];
  for (let i = 2; i < allLocs.length; i++) {
    extraBytes.push(await fillOneForm(blankBytes, formData, insured, undefined, allLocs[i], carrierMatch, resolvedNAIC));
  }

  // Merge into the first form's document so its AcroForm (pages 1, 2 and the
  // signature page) stays intact with unique field names. Each continuation
  // page is baked to static content BEFORE it is copied in: a copied page
  // would otherwise carry a second field tree with the same names as page 2,
  // and interactive viewers (Preview, Chrome) blank the duplicate. Static
  // content has no field names to collide and cannot be edited.
  const finalDoc = await PDFDocument.load(firstBytes);
  for (let i = 0; i < extraBytes.length; i++) {
    const extraDoc = await PDFDocument.load(extraBytes[i]);
    bakeWidgetsIntoPage(extraDoc, extraDoc.getPage(1));
    const [continuation] = await finalDoc.copyPages(extraDoc, [1]);
    finalDoc.insertPage(2 + i, continuation);
  }
  // Base form fields are read-only in the output except the whitelist below
  // (dates, signatures, unused coverage rows, page-1 remarks) that get
  // completed by hand at submission / signing.
  const editable = editableFieldNames(allLocs[0]);
  for (const fld of finalDoc.getForm().getFields()) {
    try {
      if (editable.has(fld.getName())) fld.disableReadOnly();
      else fld.enableReadOnly();
      propagateFieldFlags(finalDoc, fld);
    } catch {}
  }
  // Checkboxes are never on the whitelist, and PDFKit (Preview) does not
  // reliably honour the read-only flag on button widgets — bake them into the
  // base pages as static content so no viewer can toggle them.
  for (const page of finalDoc.getPages()) {
    bakeWidgetsIntoPage(finalDoc, page, (dict, ctx) => isButtonWidget(dict, ctx) || isProducerSignatureWidget(dict, ctx));
  }
  await renumberFooters(finalDoc);
  await drawProducerSignature(finalDoc);
  const finalBytes = await finalDoc.save({ updateFieldAppearances: false });

  const insuredSlug = (insured?.CompanyName || 'insured').replace(/[^a-zA-Z0-9]/g, '_');
  const blob = new Blob([finalBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ACORD_140_${insuredSlug}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// pdf-lib writes /Ff on the field dict; PDFKit (Preview) reads it from each
// widget annotation, so mirror the flags onto every kid.
function propagateFieldFlags(pdfDoc, field) {
  const flags = PDFNumber.of(field.acroField.getFlags());
  const walk = (dict) => {
    const kids = pdfDoc.context.lookupMaybe(dict.get(PDFName.of('Kids')), PDFArray);
    if (!kids) return;
    for (let i = 0; i < kids.size(); i++) {
      const kid = pdfDoc.context.lookupMaybe(kids.get(i), PDFDict);
      if (!kid) continue;
      kid.set(PDFName.of('Ff'), flags);
      walk(kid);
    }
  };
  walk(field.acroField.dict);
}

// ── Editable-field whitelist ────────────────────────────────────────────────
// Everything else in the output is read-only. Page 1: Effective Date, Date
// (top right), any coverage row left unused by the data, and the remarks
// line. Signature page: Applicant's Signature + Date, Producer's Signature.
const ALWAYS_EDITABLE = [
  'Policy_EffectiveDate_A',
  'Form_CompletionDate_A',
  'CommercialProperty_Premises_RemarkText_A',
  'NamedInsured_Signature_A',
  'NamedInsured_SignatureDate_A',
];
const PRODUCER_SIGNATURE_FIELD = 'Producer_AuthorizedRepresentative_Signature_A';
const PAGE1_COVERAGE_ROW_SUFFIXES = ['_A', '_B', '_C', '_D', '_E'];
const COVERAGE_ROW_CELLS = [
  'CommercialProperty_Premises_SubjectOfInsuranceCode',
  'CommercialProperty_Premises_LimitAmount',
  'CommercialProperty_Premises_CoinsurancePercent',
  'CommercialProperty_Premises_ValuationCode',
  'CommercialProperty_Premises_CauseOfLossCode',
  'CommercialProperty_Premises_InflationGuardPercent',
  'CommercialProperty_Premises_DeductibleAmount',
  'CommercialProperty_Premises_DeductibleTypeCode',
  'CommercialProperty_Premises_BlanketNumber',
  'CommercialProperty_Premises_FormsAndConditions',
];

function editableFieldNames(locA) {
  const names = new Set(ALWAYS_EDITABLE);
  const coverages = locA?.coverages || [];
  PAGE1_COVERAGE_ROW_SUFFIXES.forEach((suffix, i) => {
    if (coverages[i]?.subj) return; // row is populated — stays locked
    for (const cell of COVERAGE_ROW_CELLS) names.add(cell + suffix);
  });
  return names;
}

/**
 * Sequential "Page n of total" footers across the merged file. The v2
 * template carries no page numbers, so this is a plain text draw in the
 * template's footer style (Helvetica-Bold 8pt, baseline y 27): centred at
 * x 306.4 where the original "Page N of 3" sat on pages 2–3. Page 1's lower
 * footer line is the ACORD trademark notice, so its number is right-aligned
 * to the copyright line's edge (x 590.4) instead.
 */
const FOOTER_FONT_SIZE = 8;
const FOOTER_BASELINE_Y = 27;
const FOOTER_PAGE_NUMBER_CENTER_X = 306.4;
const FOOTER_RIGHT_EDGE_X = 590.4;

async function renumberFooters(pdfDoc) {
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const pages = pdfDoc.getPages();
  const total = pages.length;
  pages.forEach((page, i) => {
    const label = `Page ${i + 1} of ${total}`;
    const width = bold.widthOfTextAtSize(label, FOOTER_FONT_SIZE);
    const x = i === 0 ? FOOTER_RIGHT_EDGE_X - width : FOOTER_PAGE_NUMBER_CENTER_X - width / 2;
    page.drawText(label, { x, y: FOOTER_BASELINE_Y, size: FOOTER_FONT_SIZE, font: bold, color: rgb(0, 0, 0) });
  });
}

// ── Policy-driven rows / remarks ────────────────────────────────────────────
const fmtMoney = (n) => `$${Number(n).toLocaleString()}`;

/**
 * Enrich building 1 from the selected policy row:
 *  - NAMED STORM DED row: NamedStormDedDesc (else WindHailDedDesc) such as
 *    "3% TIV / $100K min" fills the first empty page-1 coverage row — DED from
 *    the part before "/", Forms & Conditions from the rest, DED Type WIND.
 *  - BI remark: when the buildings' BI/EE total differs from the policy's
 *    BusinessIncomeLimit, note both on the page-1 remarks line.
 */
function applyPolicyDrivenRows(locs, policy) {
  if (!policy || locs.length === 0) return locs;
  const first = { ...locs[0], coverages: (locs[0].coverages || []).map((c) => ({ ...c })) };

  const stormDesc = (policy.NamedStormDedDesc || '').trim();
  const windDesc = (policy.WindHailDedDesc || '').trim();
  const desc = stormDesc || windDesc;
  if (desc) {
    const emptyRow = first.coverages.findIndex((c) => !c.subj);
    if (emptyRow !== -1) {
      const [ded, ...rest] = desc.split('/').map((x) => x.trim());
      first.coverages[emptyRow] = {
        ...first.coverages[emptyRow],
        subj: stormDesc ? 'NAMED STORM DED' : 'WIND/HAIL DED',
        ded, dedType: 'WIND', forms: rest.join(' / '),
      };
    }
  }

  const requested = locs.reduce((sum, loc) => {
    const bi = (loc.coverages || []).find((c) => c.subj === 'BI/EE');
    return sum + (Number(bi?.limit) || 0);
  }, 0);
  const bound = Number(policy.BusinessIncomeLimit) || 0;
  if (requested > 0 && bound > 0 && requested !== bound) {
    const note = `BI/EE requested ${fmtMoney(requested)} per insured SOV; expiring bound ${fmtMoney(bound)}.`;
    first.remarkText = first.remarkText ? `${first.remarkText} ${note}` : note;
  }

  return [first, ...locs.slice(1)];
}

/** Draw the producer's signature in the script face over its (baked) field area. */
async function drawProducerSignature(pdfDoc) {
  const res = await fetch(SIGNATURE_FONT_URL);
  if (!res.ok) throw new Error(`Failed to load signature font (${res.status})`);
  pdfDoc.registerFontkit(fontkit);
  const font = await pdfDoc.embedFont(await res.arrayBuffer(), { subset: SIGNATURE_FONT_SUBSET });
  const pages = pdfDoc.getPages();
  const page = pages[pages.length - 1];
  const { x, y, width } = PRODUCER_SIGNATURE_RECT;
  // Script faces carry tall ascenders: 14pt on a baseline 2pt above the cell
  // floor clears the "PRODUCER'S SIGNATURE" label printed at the cell top.
  let size = SIGNATURE_MAX_SIZE;
  while (size > 8 && font.widthOfTextAtSize(SIGNATURE_TEXT, size) > width - 8) size -= 1;
  page.drawText(SIGNATURE_TEXT, { x: x + 6, y: y + 2, size, font, color: rgb(0, 0, 0) });
}
// Producer signature widget on the v2 template's signature page.
const PRODUCER_SIGNATURE_RECT = { x: 21.8, y: 59.4, width: 231, height: 12.6 };
const SIGNATURE_MAX_SIZE = 14;

function widgetFieldName(dict, ctx) {
  let d = dict;
  for (let depth = 0; d && depth < 8; depth++) {
    const t = d.get(PDFName.of('T'));
    if (t) return t.decodeText();
    d = ctx.lookupMaybe(d.get(PDFName.of('Parent')), PDFDict);
  }
  return '';
}
function isProducerSignatureWidget(dict, ctx) {
  return widgetFieldName(dict, ctx) === PRODUCER_SIGNATURE_FIELD;
}

/** Field type (/FT) of a widget, inherited from its parent chain. */
function isButtonWidget(dict, ctx) {
  let d = dict;
  for (let depth = 0; d && depth < 8; depth++) {
    const ft = d.get(PDFName.of('FT'));
    if (ft) return ft === PDFName.of('Btn');
    d = ctx.lookupMaybe(d.get(PDFName.of('Parent')), PDFDict);
  }
  return false;
}

/**
 * Replace widget annotations on `page` (all of them, or those `shouldBake`
 * selects) with their own appearance streams drawn as page content, then
 * drop those widgets from the page. Per-page, per-widget flatten — pdf-lib's
 * form.flatten() is all-or-nothing, and the base pages need only their
 * checkboxes baked while the whitelisted fields stay live.
 */
function bakeWidgetsIntoPage(pdfDoc, page, shouldBake = () => true) {
  const ctx = pdfDoc.context;
  const annots = page.node.Annots();
  if (!annots) return;
  const keep = [];
  for (let i = 0; i < annots.size(); i++) {
    const ref = annots.get(i);
    const dict = ctx.lookupMaybe(ref, PDFDict);
    if (!dict) continue;
    if (dict.get(PDFName.of('Subtype')) !== PDFName.of('Widget') || !shouldBake(dict, ctx)) { keep.push(ref); continue; }
    const rect = ctx.lookupMaybe(dict.get(PDFName.of('Rect')), PDFArray);
    const ap = ctx.lookupMaybe(dict.get(PDFName.of('AP')), PDFDict);
    let normal = ap && ap.get(PDFName.of('N'));
    if (normal) {
      const resolved = ctx.lookup(normal);
      if (resolved instanceof PDFDict && !(resolved instanceof PDFStream)) {
        // Checkbox-style appearance: pick the stream for the current state.
        const state = dict.get(PDFName.of('AS')) || PDFName.of('Off');
        normal = resolved.get(state);
      }
    }
    const stream = normal ? ctx.lookupMaybe(normal, PDFStream) : null;
    if (rect && stream) {
      const { x, y, width, height } = rect.asRectangle();
      // Scale the appearance's BBox onto the widget rectangle (normally 1:1).
      const bbox = ctx.lookupMaybe(stream.dict.get(PDFName.of('BBox')), PDFArray);
      const bw = bbox ? Math.abs(bbox.lookup(2, PDFNumber).asNumber() - bbox.lookup(0, PDFNumber).asNumber()) : width;
      const bh = bbox ? Math.abs(bbox.lookup(3, PDFNumber).asNumber() - bbox.lookup(1, PDFNumber).asNumber()) : height;
      const key = page.node.newXObject('BakedWidget', normal);
      page.pushOperators(
        pushGraphicsState(),
        concatTransformationMatrix(bw ? width / bw : 1, 0, 0, bh ? height / bh : 1, x, y),
        drawObject(key),
        popGraphicsState(),
      );
    }
    // Widget dropped from the page either way — no field name survives here.
  }
  page.node.set(PDFName.of('Annots'), ctx.obj(keep));
}

async function fillOneForm(blankBytes, formData, insured, locA, locB, carrierMatch, resolvedNAIC) {
  const pdfDoc = await PDFDocument.load(blankBytes);
  const form = pdfDoc.getForm();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  // Template fields carry a fixed 8pt DA. Preview's live-field renderer pads
  // the box ~4pt a side and clips values that just fit (e.g. "100,000" in the
  // 33pt-wide DED cells → "100,00"), so any value that would overflow at 8pt
  // inside that padding is switched to auto font size, which every viewer
  // fits to the box.
  const TEMPLATE_FONT_SIZE = 8;
  const VIEWER_BOX_PADDING = 8;
  function setText(name, value) {
    if (!value && value !== 0) return;
    try {
      const field = form.getTextField(name);
      const text = String(value);
      field.setText(text);
      try {
        const widget = field.acroField.getWidgets()[0];
        const boxWidth = widget ? widget.getRectangle().width - VIEWER_BOX_PADDING : Infinity;
        if (font.widthOfTextAtSize(text, TEMPLATE_FONT_SIZE) > boxWidth) field.setFontSize(0);
      } catch {}
      try { field.updateAppearances(font); } catch {}
    } catch {}
  }

  function setCheck(name) {
    try { form.getCheckBox(name).check(); } catch {}
  }

  const f = formData;
  // Numbers get thousands separators; descriptive values ("3% TIV") pass through.
  const fmtNum = (v) => {
    if (v === '' || v == null) return '';
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString() : String(v);
  };

  // ============ HEADER ============
  // Form_CompletionDate_A intentionally left blank — producer/applicant fills at signing.
  setText('Producer_FullName_A', 'Fragos Insurance Group LLC');
  setText('Policy_PolicyNumberIdentifier_A', f.policyNumber);
  setText('Policy_EffectiveDate_A', f.effectiveDate);
  setText('Insurer_FullName_A', f.carrier);
  // NAIC resolved via carrier shortname map (reverse-lookup). Lloyd's and
  // other no-US-NAIC carriers correctly yield blank.
  setText('Insurer_NAICCode_A', resolvedNAIC);
  setText('NamedInsured_FullName_A', insured?.CompanyName || '');
  // Agency Customer ID — template field has 3 widgets (one per page). Plain
  // setText + updateAppearances propagates to all three via the parent field.
  if (insured?.id) setText('Producer_CustomerIdentifier_A', `FIG-${insured.id}`);

  if (carrierMatch) {
    setText('Insurer_MailingAddress_AddressLineOne_A', carrierMatch.Address);
    setText('Insurer_MailingAddress_CityName_A', carrierMatch.City);
    setText('Insurer_MailingAddress_StateOrProvinceCode_A', carrierMatch.State);
    setText('Insurer_MailingAddress_PostalCode_A', carrierMatch.Zip);
  }

  // ============ BLANKET SUMMARY (4 rows A-D) ============
  const blanketSuffixes = ['_A', '_B', '_C', '_D'];
  if (f.blankets) {
    f.blankets.forEach((b, i) => {
      if (i > 3) return;
      const s = blanketSuffixes[i];
      setText(`CommercialProperty_Summary_BlanketNumberIdentifier${s}`, b.num);
      setText(`CommercialProperty_Summary_BlanketLimitAmount${s}`, fmtNum(b.amount));
      setText(`CommercialCoverage_Summary_BlanketTypeDescription${s}`, b.type);
    });
  }

  // ============ LOCATION A & B ============
  fillLocation(locA, '_A', ['_A','_B','_C','_D','_E']);
  fillLocation(locB, '_B', ['_G','_H','_I','_J','_K']);

  function fillLocation(loc, locSuffix, covSuffixes) {
    if (!loc) return;

    // Premises
    setText(`CommercialStructure_Location_ProducerIdentifier${locSuffix}`, loc.locNum);
    setText(`CommercialStructure_PhysicalAddress_LineOne${locSuffix}`, loc.address);
    setText(`CommercialStructure_Building_ProducerIdentifier${locSuffix}`, loc.bldgNum);
    setText(`CommercialStructure_Building_SublocationDescription${locSuffix}`, loc.bldgDesc);

    // Coverage schedule (5 rows)
    if (loc.coverages) {
      loc.coverages.forEach((c, i) => {
        if (i > 4) return;
        const s = covSuffixes[i];
        setText(`CommercialProperty_Premises_SubjectOfInsuranceCode${s}`, c.subj);
        setText(`CommercialProperty_Premises_LimitAmount${s}`, fmtNum(c.limit));
        setText(`CommercialProperty_Premises_CoinsurancePercent${s}`, c.coins);
        setText(`CommercialProperty_Premises_ValuationCode${s}`, c.val);
        setText(`CommercialProperty_Premises_CauseOfLossCode${s}`, c.cause);
        setText(`CommercialProperty_Premises_InflationGuardPercent${s}`, c.inflGuard);
        setText(`CommercialProperty_Premises_DeductibleAmount${s}`, fmtNum(c.ded));
        setText(`CommercialProperty_Premises_DeductibleTypeCode${s}`, c.dedType);
        setText(`CommercialProperty_Premises_BlanketNumber${s}`, c.blanketNum);
        setText(`CommercialProperty_Premises_FormsAndConditions${s}`, c.forms);
      });
    }

    // BI/Extra Expense & Value Reporting checkboxes
    if (loc.biExtraExpense) setCheck(`CommercialProperty_Attachment_BusinessIncomeExtraExpenseIndicator${locSuffix}`);
    if (loc.valueReporting) setCheck(`CommercialProperty_Attachment_ValueReportingIndicator${locSuffix}`);

    // Spoilage
    setText(`CommercialProperty_Spoilage_YesNoCode${locSuffix}`, loc.spoilage);
    setText(`CommercialProperty_Spoilage_PropertyDescription${locSuffix}`, loc.spoilageDesc);
    setText(`CommercialProperty_Spoilage_LimitAmount${locSuffix}`, fmtNum(loc.spoilageLimit));
    setText(`CommercialProperty_Spoilage_DeductibleAmount${locSuffix}`, loc.spoilageDed);
    setText(`CommercialProperty_Spoilage_RefrigeratorMaintenanceCode${locSuffix}`, loc.spoilageRefrig);
    setText(`CommercialProperty_Premises_OptionsDescription${locSuffix}`, loc.optionsDesc);

    // Spoilage sub-checkboxes
    if (loc.spoilageBreakdown) setCheck(`CommercialProperty_Premises_BreakdownOrContaminationIndicator${locSuffix}`);
    if (loc.spoilagePower) setCheck(`CommercialProperty_Premises_PowerOutageIndicator${locSuffix}`);
    if (loc.spoilageSelling) setCheck(`CommercialProperty_Premises_SellingPriceIndicator${locSuffix}`);
    if (loc.spoilageOther) setCheck(`CommercialProperty_Premises_OtherIndicator${locSuffix}`);

    // Sinkhole
    if (loc.sinkhole === 'Y') setCheck(`CommercialPropertyCoverage_SinkHoleCollapse_YesIndicator${locSuffix}`);
    if (loc.sinkhole === 'N') setCheck(`CommercialPropertyCoverage_SinkHoleCollapse_NoIndicator${locSuffix}`);
    setText(`CommercialPropertyCoverage_SinkHoleCollapse_LimitAmount${locSuffix}`, fmtNum(loc.sinkholeLimit));

    // Mine Subsidence
    if (loc.mine === 'Y') setCheck(`CommercialPropertyCoverage_MineSubsidence_YesIndicator${locSuffix}`);
    if (loc.mine === 'N') setCheck(`CommercialPropertyCoverage_MineSubsidenceOption_NoIndicator${locSuffix}`);
    setText(`CommercialPropertyCoverage_MineSubsidence_LimitAmount${locSuffix}`, fmtNum(loc.mineLimit));

    // Historical Property
    if (loc.historicalProperty) setCheck(`BuildingFeatures_HistoricalPropertyIndicator${locSuffix}`);
    setText(`Construction_OpenSidesCount${locSuffix}`, loc.openSides);

    // Construction
    setText(`Construction_ConstructionCode${locSuffix}`, loc.constructionCode);
    setText(`Construction_StoreyCount${locSuffix}`, loc.stories);
    setText(`Construction_BasementCount${locSuffix}`, loc.basements);
    setText(`CommercialStructure_BuiltYear${locSuffix}`, loc.yearBuilt);
    setText(`Construction_BuildingArea${locSuffix}`, loc.totalArea);
    setText(`BuildingFireProtection_HydrantDistanceFeetCount${locSuffix}`, loc.hydrantDist);
    setText(`BuildingFireProtection_FireStationDistanceMileCount${locSuffix}`, loc.fireStationDist);
    setText(`BuildingFireProtection_FireDistrictName${locSuffix}`, loc.fireDistrictName);
    setText(`BuildingFireProtection_FireDistrictCode${locSuffix}`, loc.fireDistrictCode);
    setText(`BuildingFireProtection_ProtectionClassCode${locSuffix}`, loc.protectionClass);
    setText(`Construction_BuildingCodeEffectivenessGradeCode${locSuffix}`, loc.bceg);
    setText(`CommercialStructure_TaxCode${locSuffix}`, loc.taxCode);
    setText(`Construction_RoofMaterialCode${locSuffix}`, loc.roofType);
    setText(`BuildingOccupancy_OtherOccupanciesDescription${locSuffix}`, loc.otherOccupancies);

    // Improvements
    if (loc.impWiring) setCheck(`BuildingImprovement_WiringIndicator${locSuffix}`);
    setText(`BuildingImprovement_WiringYear${locSuffix}`, loc.impWiringYr);
    if (loc.impRoofing) setCheck(`BuildingImprovement_RoofingIndicator${locSuffix}`);
    setText(`BuildingImprovement_RoofingYear${locSuffix}`, loc.impRoofingYr);
    if (loc.impPlumbing) setCheck(`BuildingImprovement_PlumbingIndicator${locSuffix}`);
    setText(`BuildingImprovement_PlumbingYear${locSuffix}`, loc.impPlumbingYr);
    if (loc.impHeating) setCheck(`BuildingImprovement_HeatingIndicator${locSuffix}`);
    setText(`BuildingImprovement_HeatingYear${locSuffix}`, loc.impHeatingYr);
    if (loc.impOther) setCheck(`BuildingImprovement_OtherIndicator${locSuffix}`);
    setText(`BuildingImprovement_OtherDescription${locSuffix}`, loc.impOtherDesc);
    setText(`BuildingImprovement_OtherYear${locSuffix}`, loc.impOtherYr);

    // Wind Class
    if (loc.windClass === 'Resistive') setCheck(`CommercialStructure_WindClass_ResistiveIndicator${locSuffix}`);
    if (loc.windClass === 'Semi-Resistive') setCheck(`CommercialStructure_WindClass_SemiResistiveIndicator${locSuffix}`);
    if (loc.windClass === 'Other') {
      setCheck(`CommercialStructure_WindClass_OtherIndicator${locSuffix}`);
      setText(`CommercialStructure_WindClass_OtherDescription${locSuffix}`, loc.windClassOther);
    }

    // Solid Fuel Heater
    if (loc.solidFuelHeater) setCheck(`BuildingFeatures_SolidFuelHeaterIndicator${locSuffix}`);
    setText(`BuildingFeatures_SolidFuelHeaterManufacturerName${locSuffix}`, loc.solidFuelMfg);
    setText(`BuildingFeatures_SolidFuelHeaterInstallationDate${locSuffix}`, loc.solidFuelDate);

    // Primary Heat — note: boiler insured codes use _A/_B for loc A, _C/_D for loc B
    const heatCodeSuffix1 = locSuffix === '_A' ? '_A' : '_C';
    const heatCodeSuffix2 = locSuffix === '_A' ? '_B' : '_D';
    if (loc.primaryHeat === 'Boiler') setCheck(`CommercialStructure_PrimaryHeat_BoilerIndicator${locSuffix}`);
    if (loc.primaryHeat === 'Solid Fuel') setCheck(`CommercialStructure_PrimaryHeat_SolidFuelIndicator${locSuffix}`);
    if (loc.primaryHeat === 'Other') {
      setCheck(`CommercialStructure_PrimaryHeat_OtherIndicator${locSuffix}`);
      setText(`CommercialStructure_PrimaryHeat_OtherDescription${locSuffix}`, loc.primaryHeatOther);
    }
    setText(`CommercialStructure_HeatingBoilerInsuredElsewhereCode${heatCodeSuffix1}`, loc.primaryBoilerInsured);

    // Secondary Heat
    if (loc.secondaryHeat === 'Boiler') setCheck(`CommercialStructure_SecondaryHeat_BoilerIndicator${locSuffix}`);
    if (loc.secondaryHeat === 'Solid Fuel') setCheck(`CommercialStructure_SecondaryHeat_SolidFuelIndicator${locSuffix}`);
    if (loc.secondaryHeat === 'Other') {
      setCheck(`CommercialStructure_SecondaryHeat_OtherIndicator${locSuffix}`);
      setText(`CommercialStructure_SecondaryHeat_OtherDescription${locSuffix}`, loc.secondaryHeatOther);
    }
    setText(`CommercialStructure_HeatingBoilerInsuredElsewhereCode${heatCodeSuffix2}`, loc.secondaryBoilerInsured);

    // Exposures
    setText(`BuildingExposure_RightDescription${locSuffix}`, loc.expRight);
    setText(`BuildingExposure_RightDistance${locSuffix}`, loc.expRightDist);
    setText(`BuildingExposure_LeftDescription${locSuffix}`, loc.expLeft);
    setText(`BuildingExposure_LeftDistance${locSuffix}`, loc.expLeftDist);
    setText(`BuildingExposure_FrontDescription${locSuffix}`, loc.expFront);
    setText(`BuildingExposure_FrontDistance${locSuffix}`, loc.expFrontDist);
    setText(`BuildingExposure_RearDescription${locSuffix}`, loc.expRear);
    setText(`BuildingExposure_RearDistance${locSuffix}`, loc.expRearDist);

    // Burglar Alarm
    setText(`Alarm_Burglar_AlarmDescription${locSuffix}`, loc.burglarType);
    setText(`Alarm_Burglar_CertificateIdentifier${locSuffix}`, loc.burglarCert);
    setText(`Alarm_Burglar_CertificateExpirationDate${locSuffix}`, loc.burglarExp);
    setText(`Alarm_Burglar_InstalledAndServicedByName${locSuffix}`, loc.burglarInstaller);
    setText(`Alarm_Burglar_ProtectionExtentCode${locSuffix}`, loc.burglarExtent);
    setText(`Alarm_Burglar_GradeCode${locSuffix}`, loc.burglarGrade);

    // Burglar alarm type checkboxes
    if ((loc.burglarType || '').toLowerCase().includes('central')) setCheck(`Alarm_Burglar_CentralStationIndicator${locSuffix}`);
    if ((loc.burglarType || '').toLowerCase().includes('key')) setCheck(`Alarm_Burglar_WithKeysIndicator${locSuffix}`);
    if ((loc.burglarType || '').toLowerCase().includes('local')) setCheck(`Burglar_LocalGongIndicator${locSuffix}`);

    // Security
    setText(`BuildingSecurity_GuardWatchmenCount${locSuffix}`, loc.guardCount);
    if ((loc.guardType || '').toLowerCase().includes('clock')) setCheck(`BuildingSecurity_GuardWatchmenClockHourlyIndicator${locSuffix}`);
    if ((loc.guardType || '').toLowerCase().includes('other')) {
      setCheck(`BuildingSecurity_GuardWatchmenOtherIndicator${locSuffix}`);
      setText(`BuildingSecurity_GuardWatchmenOtherDescription${locSuffix}`, loc.guardType);
    }

    // Fire Protection
    setText(`BuildingFireProtection_Alarm_ProtectionDescription${locSuffix}`, loc.fireProtDesc);
    setText(`BuildingFireProtection_Alarm_SprinklerPercent${locSuffix}`, loc.sprinklerPct);
    setText(`BuildingFireProtection_Alarm_ManufacturerName${locSuffix}`, loc.fireMfg);
    if (loc.fireCentral) setCheck(`BuildingFireProtection_Alarm_CentralStationIndicator${locSuffix}`);
    if (loc.fireLocal) setCheck(`BuildingFireProtection_Alarm_LocalGongIndicator${locSuffix}`);

    // Additional Interest
    const aiSuffix = locSuffix; // _A or _B
    if (loc.interestType) {
      const it = loc.interestType.toLowerCase();
      if (it.includes('lenders')) setCheck(`AdditionalInterest_Interest_LendersLossPayableIndicator${aiSuffix}`);
      if (it.includes('loss payee')) setCheck(`AdditionalInterest_Interest_LossPayeeIndicator${aiSuffix}`);
      if (it.includes('mortgagee')) setCheck(`AdditionalInterest_Interest_MortgageeIndicator${aiSuffix}`);
    }
    setText(`AdditionalInterest_FullName${aiSuffix}`, loc.interestName);
    setText(`AdditionalInterest_MailingAddress_LineOne${aiSuffix}`, loc.interestAddress);
    setText(`AdditionalInterest_MailingAddress_CityName${aiSuffix}`, loc.interestCity);
    setText(`AdditionalInterest_MailingAddress_StateOrProvinceCode${aiSuffix}`, loc.interestState);
    setText(`AdditionalInterest_MailingAddress_PostalCode${aiSuffix}`, loc.interestZip);
    setText(`AdditionalInterest_AccountNumberIdentifier${aiSuffix}`, loc.interestLoanNum);

    // Remarks (only on loc A page — field is _A for page 1, _C for page 2)
    const remarkSuffix = locSuffix === '_A' ? '_A' : '_C';
    setText(`CommercialProperty_Premises_RemarkText${remarkSuffix}`, loc.remarkText);
  }

  // ============ REMARKS (page 2 bottom) ============
  setText('CommercialPropertyLineOfBusiness_RemarkText_A', f.remarks);

  // ============ SIGNATURE (page 3) ============
  // Producer signature is baked (drawProducerSignature). Applicant signature + date
  // intentionally LEFT BLANK per DocuSign routing practice — live signature
  // and date are captured at signing time.
  // Producer signature is drawn in script and baked — see drawProducerSignature.
  setText('Producer_AuthorizedRepresentative_FullName_A', 'Marc Fragos');
  setText('Producer_StateLicenseIdentifier_A', '');
  setText('Producer_NationalIdentifier_A', '');

  // Regenerate appearance streams for every field touched above so page 2
  // (and the merged continuation pages) render in Preview / Acrobat / Chrome.
  form.updateFieldAppearances(font);

  return await pdfDoc.save({ updateFieldAppearances: false });
}
